import type { GbTier } from "@/lib/pricing-utils";

export const PRICING_VERIFIED_DATE = "July 19, 2026";

export const SOAX_URL = "https://soax.com";
export const SOAX_MOBILE_URL = "https://soax.com/proxies/mobile";
export const SOAX_RESIDENTIAL_URL = "https://soax.com/proxies/residential";
export const SOAX_PRICING_URL = "https://soax.com/pricing";

export type ProxyProduct = "mobile" | "residential";

export interface SoaxPlan {
  name: string;
  monthlyMinimum: number;
  perGb: number;
}

// SOAX prices residential and mobile traffic at the same rate under one plan
// ladder — there is no separate mobile price sheet. But the per-GB rate does
// vary by a 3-tier country grouping (Tier 1 highest, Tier 3 lowest); of
// Proxidize's 5 mobile countries, US/UK/Canada/Germany fall in SOAX's Tier 1
// and India falls in SOAX's cheaper Tier 2. Each plan is a monthly minimum
// commitment billed at that tier's flat per-GB rate; usage beyond the
// included credits keeps billing at the same rate, so cost for a given plan
// is max(monthlyMinimum, gb * perGb). Source: soax.com/pricing.
export const SOAX_TIER1_PLANS: SoaxPlan[] = [
  { name: "Sandbox", monthlyMinimum: 0, perGb: 5.0 },
  { name: "Builder", monthlyMinimum: 200, perGb: 3.0 },
  { name: "Team", monthlyMinimum: 500, perGb: 2.2 },
  { name: "Scale", monthlyMinimum: 1500, perGb: 1.5 },
  { name: "Enterprise", monthlyMinimum: 3000, perGb: 0.85 },
];

// Tier 2 covers India (among ~90 other countries) — same plan minimums, lower
// per-GB rate than Tier 1.
export const SOAX_TIER2_PLANS: SoaxPlan[] = [
  { name: "Sandbox", monthlyMinimum: 0, perGb: 3.75 },
  { name: "Builder", monthlyMinimum: 200, perGb: 2.25 },
  { name: "Team", monthlyMinimum: 500, perGb: 1.65 },
  { name: "Scale", monthlyMinimum: 1500, perGb: 1.15 },
  { name: "Enterprise", monthlyMinimum: 3000, perGb: 0.55 },
];

export const SOAX_TIER_NOTE =
  "SOAX's per-GB rate depends on a 3-tier country grouping. Of Proxidize's 5 mobile countries, the US, UK, Canada, and Germany fall in SOAX's Tier 1 (the rate used throughout this page); India falls in SOAX's cheaper Tier 2 ($3.75/GB Sandbox down to $0.55/GB Enterprise), which lowers the mobile crossover point to roughly 250 GB/month for India specifically.";

export const SOAX_ROLLOVER_NOTE =
  "SOAX credit rollover runs 60 days on monthly billing, extending to 365 days on annual enterprise terms.";

export const SOAX_TRIAL_NOTE = "SOAX offers a $1.99 trial for 400 MB over 3 days.";

// Every SOAX plan on soax.com/pricing is listed as "$X/mo + VAT" — Proxidize's
// advertised prices don't add a separate VAT line item.
export const SOAX_VAT_NOTE =
  "SOAX's published prices are shown excluding VAT (\"+ VAT\" on their pricing page); Proxidize's advertised prices don't add a separate VAT line.";

export const PROXIDIZE_MOBILE_FALLBACK_TIERS: GbTier[] = [
  { gb: 25, monthlyPPG: 2, totalPrice: 50 },
  { gb: 50, monthlyPPG: 2, totalPrice: 100 },
  { gb: 100, monthlyPPG: 2, totalPrice: 200 },
  { gb: 200, monthlyPPG: 2, totalPrice: 400 },
  { gb: 500, monthlyPPG: 2, totalPrice: 1000 },
  { gb: 1000, monthlyPPG: 2, totalPrice: 2000 },
  { gb: 2000, monthlyPPG: 2, totalPrice: 4000 },
  { gb: 5000, monthlyPPG: 2, totalPrice: 10000 },
  { gb: 10000, monthlyPPG: 2, totalPrice: 20000 },
];

export const PROXIDIZE_RESIDENTIAL_FALLBACK_TIERS: GbTier[] = [
  { gb: 25, monthlyPPG: 1, totalPrice: 25 },
  { gb: 50, monthlyPPG: 1, totalPrice: 50 },
  { gb: 100, monthlyPPG: 1, totalPrice: 100 },
  { gb: 200, monthlyPPG: 1, totalPrice: 200 },
  { gb: 500, monthlyPPG: 1, totalPrice: 500 },
  { gb: 1000, monthlyPPG: 1, totalPrice: 1000 },
  { gb: 2000, monthlyPPG: 0.95, totalPrice: 1900 },
  { gb: 5000, monthlyPPG: 0.95, totalPrice: 4750 },
  { gb: 10000, monthlyPPG: 0.9, totalPrice: 9000 },
];

export interface SoaxCost {
  cost: number;
  planLabel: string;
}

// SOAX bills the cheapest plan that fits the requested volume — cost per plan
// is max(monthlyMinimum, gb * perGb). The same function applies to both
// products since SOAX doesn't price mobile and residential separately.
// Defaults to Tier 1 (US, UK, Canada, Germany) — pass SOAX_TIER2_PLANS for
// India-specific comparisons.
export function soaxMonthlyCost(gb: number, plans: SoaxPlan[] = SOAX_TIER1_PLANS): SoaxCost {
  let best: SoaxCost = { cost: Infinity, planLabel: "" };
  for (const plan of plans) {
    const cost = Math.round(Math.max(plan.monthlyMinimum, gb * plan.perGb) * 100) / 100;
    if (cost < best.cost) best = { cost, planLabel: `${plan.name} plan` };
  }
  return best;
}
